import { Collection, ModelSearchResult } from "@mcleod/core";
import { RowGroupMembership } from "../models/ModelGroupMembership";
import { RowPermsUserGroup } from "../models/ModelPermsUserGroup";
import { RowUsers } from "../models/ModelUsers";
import { UserOrGroup } from "./UserOrGroup";

/**
 * This class is used to cache the users and user_group data need by Permissions Manager.
 */
export class PermsUserAndGroupCache {

    private _lmeUsers: Collection<RowUsers>;
    private _webUsers: Collection<RowUsers>;
    private _allUsers: Collection<RowUsers>;
    private _userGroups: Collection<RowPermsUserGroup>;
    private _userGroupMemberships: Collection<RowGroupMembership[]>;

    populateUserGroupData(data: any[]) {
        const userGroupRows = data?.map((row) => new RowPermsUserGroup().setValues(row));
        const groupResult = new ModelSearchResult();
        groupResult.modelRows = userGroupRows;
        const membershipResult = new ModelSearchResult();

        membershipResult.modelRows = groupResult.modelRows?.flatMap((row) =>
            row.get("group_membership")?.map((membershipData: any) =>
                new RowGroupMembership().setValues(membershipData)
            ) ?? []
        ) ?? [];

        this._userGroups = groupResult.mapRowsByField("id");
        this._userGroupMemberships = membershipResult.groupRowsByField("user_id");
    }

    populateUserData(data: any[]) {
        const userRows = data?.map((row) => new RowUsers().setValues(row));
        this.buildUserCollections(userRows);
    }

    private buildUserCollections(userRows: RowUsers[]) {
        this._lmeUsers = {};
        this._webUsers = {};
        this._allUsers = {};
        userRows?.forEach(user => {
            const userId = user.get("id");
            this._allUsers[userId] = user;
            if (user.get("web_user_type") === "U") {
                this._lmeUsers[userId] = user;
            } else {
                this._webUsers[userId] = user;
            }
        });
    }

    get lmeUsers(): Collection<RowUsers> {
        return this._lmeUsers;
    }

    get webUsers(): Collection<RowUsers> {
        return this._webUsers;
    }

    get allUsers(): Collection<RowUsers> {
        return this._allUsers;
    }

    get userGroups(): Collection<RowPermsUserGroup> {
        return this._userGroups;
    }

    get userGroupMemberships(): Collection<RowGroupMembership[]> {
        return this._userGroupMemberships;
    }

    public isUserInGroup(userId: string, groupId: string): boolean {
        return this._userGroupMemberships[userId]?.some(
            (membership) => membership.get("user_group_id") === groupId
        );
    }

    public getDisplayName(userOrGroup: UserOrGroup, userOrGroupId: string): string {
        let result = null;
        if (userOrGroup === "U") {
            result = this.allUsers[userOrGroupId]?.get("name");
        } else {
            result = this.userGroups[userOrGroupId]?.get("descr");
        }
        return result ?? userOrGroupId;
    }

}
